/**
 * Investigation API Client
 *
 * All functions use the shared apiClient which handles
 * Bearer token injection and error logging.
 */

import { apiClient } from './client';
import type {
  InvestigationListResponse,
  InvestigationDetail,
  InvestigationSummary,
  EvidenceEnvelopeManifest,
  ClaimGraphSummary,
  CounterSignalFeedResponse,
  DriftFeedResponse,
  EvidenceSubmitRequest,
  EvidenceItem,
  ClaimCreateRequest,
  ClaimNode,
  CounterSignalCreateRequest,
  CounterSignal,
  DriftCreateRequest,
  DriftEvent,
  ReadinessResponse,
  CertificateRecordResponse,
  CertificateBuildResponse,
  AnchorBatchResponse,
  AdmissionResult,
  ShadowBootstrapResult,
  ShadowExecutionResult,
  PromotionResult,
} from '../types/investigation';

// ── List & Detail ────────────────────────────────────────────────────────

export async function fetchInvestigations(): Promise<InvestigationListResponse> {
  const { data } = await apiClient.get<InvestigationListResponse>(
    '/api/v1/investigations/'
  );
  return data;
}

export async function fetchInvestigation(
  investigationId: string
): Promise<InvestigationDetail> {
  const { data } = await apiClient.get<InvestigationDetail>(
    `/api/v1/investigations/${investigationId}`
  );
  return data;
}

// ── Sub-resources ────────────────────────────────────────────────────────

export async function fetchEvidence(
  investigationId: string
): Promise<EvidenceEnvelopeManifest> {
  const { data } = await apiClient.get<EvidenceEnvelopeManifest>(
    `/api/v1/investigations/${investigationId}/evidence`
  );
  return data;
}

export async function fetchClaims(
  investigationId: string
): Promise<ClaimGraphSummary> {
  const { data } = await apiClient.get<ClaimGraphSummary>(
    `/api/v1/investigations/${investigationId}/claims`
  );
  return data;
}

export async function fetchCounterSignals(
  investigationId: string
): Promise<CounterSignalFeedResponse> {
  const { data } = await apiClient.get<CounterSignalFeedResponse>(
    `/api/v1/investigations/${investigationId}/counter-signals`
  );
  return data;
}

export async function fetchDrift(
  investigationId: string
): Promise<DriftFeedResponse> {
  const { data } = await apiClient.get<DriftFeedResponse>(
    `/api/v1/investigations/${investigationId}/drift`
  );
  return data;
}

export async function fetchCertificate(
  investigationId: string
): Promise<CertificateRecordResponse> {
  const { data } = await apiClient.get<CertificateRecordResponse>(
    `/api/v1/investigations/${investigationId}/certificate`
  );
  return data;
}

// ── Create ───────────────────────────────────────────────────────────────

export async function createInvestigation(body: {
  theatre_id?: string;
  construct_id?: string;
  inquiry_class?: string;
  template_id?: string;
  domain_filters?: string[];
  stop_condition?: string;
  stop_config?: Record<string, unknown>;
  geo?: {
    scope: string;
    center?: readonly [number, number] | null;
    radius_m?: number | null;
    label?: string | null;
    derivation?: string;
  } | null;
  source_signal_ids?: string[];
  committed_sources?: string[];
}): Promise<InvestigationSummary> {
  const { data } = await apiClient.post<InvestigationSummary>(
    '/api/v1/investigations/',
    body
  );
  return data;
}

// ── Status Transitions ──────────────────────────────────────────────────

export async function dismissInvestigation(
  investigationId: string,
): Promise<InvestigationSummary> {
  const { data } = await apiClient.patch<InvestigationSummary>(
    `/api/v1/investigations/${investigationId}/status`,
    { status: 'DISMISSED' },
  );
  return data;
}

export async function reactivateInvestigation(
  investigationId: string,
): Promise<InvestigationSummary> {
  const { data } = await apiClient.patch<InvestigationSummary>(
    `/api/v1/investigations/${investigationId}/status`,
    { status: 'ACTIVE' },
  );
  return data;
}

// ── Mutations (submit evidence, claims, counter-signals, drift) ─────────

export async function submitEvidence(
  investigationId: string,
  body: EvidenceSubmitRequest,
): Promise<EvidenceItem> {
  const { data } = await apiClient.post<EvidenceItem>(
    `/api/v1/investigations/${investigationId}/evidence`,
    body,
  );
  return data;
}

// Cycle 082: Epistemic transitions
export async function promoteEvidence(
  investigationId: string,
  evidenceId: string,
): Promise<Record<string, unknown>> {
  const { data } = await apiClient.post(
    `/api/v1/investigations/${investigationId}/evidence/${evidenceId}/promote`,
  );
  return data;
}

export async function detachEvidence(
  investigationId: string,
  evidenceId: string,
): Promise<Record<string, unknown>> {
  const { data } = await apiClient.post(
    `/api/v1/investigations/${investigationId}/evidence/${evidenceId}/detach`,
  );
  return data;
}

export async function submitClaim(
  investigationId: string,
  body: ClaimCreateRequest,
): Promise<ClaimNode> {
  const { data } = await apiClient.post<ClaimNode>(
    `/api/v1/investigations/${investigationId}/claims`,
    body,
  );
  return data;
}

export async function submitCounterSignal(
  investigationId: string,
  body: CounterSignalCreateRequest,
): Promise<CounterSignal> {
  const { data } = await apiClient.post<CounterSignal>(
    `/api/v1/investigations/${investigationId}/counter-signals`,
    body,
  );
  return data;
}

export async function submitDrift(
  investigationId: string,
  body: DriftCreateRequest,
): Promise<DriftEvent> {
  const { data } = await apiClient.post<DriftEvent>(
    `/api/v1/investigations/${investigationId}/drift`,
    body,
  );
  return data;
}

export async function acknowledgeDrift(
  investigationId: string,
  driftId: string,
): Promise<DriftEvent> {
  const { data } = await apiClient.patch<DriftEvent>(
    `/api/v1/investigations/${investigationId}/drift/${driftId}/acknowledge`,
  );
  return data;
}

// ── Environment projection (Cycle 075) ──────────────────────────────────

export interface InvestigationProjectionResponse {
  investigation_id: string;
  theatre_id: string;
  projected_at?: string;
  status: 'projected' | 'removed';
}

export async function projectInvestigationToEnvironment(
  investigationId: string,
  theatreId: string,
): Promise<InvestigationProjectionResponse> {
  const { data } = await apiClient.post<InvestigationProjectionResponse>(
    `/api/v1/investigations/${investigationId}/project-to-environment`,
    { theatre_id: theatreId },
  );
  return data;
}

/**
 * ensureCompositionAndProject — GAP-2 fallback (sprint-236, SDD §4.1.4).
 *
 * Promote with a falsy `theatre_id` used to 400 backend-side
 * (`investigation_routes.py:1846` — "theatre_id is required"). When no
 * theatre is linked, create a blank composition first (the same
 * `POST /api/v1/environment/compositions {preset: null}` the picker's
 * "New composition" row uses), then project into the new id. Reuses the
 * existing `projectInvestigationToEnvironment` call — no duplicate
 * projection path.
 */
export async function ensureCompositionAndProject(
  investigationId: string,
  theatreId?: string | null,
): Promise<InvestigationProjectionResponse> {
  let targetTheatreId = theatreId;
  if (!targetTheatreId) {
    const { data: created } = await apiClient.post<{ theatre_id: string }>(
      '/api/v1/environment/compositions',
      { preset: null },
    );
    targetTheatreId = created.theatre_id;
  }
  return projectInvestigationToEnvironment(investigationId, targetTheatreId);
}

export async function removeInvestigationFromEnvironment(
  investigationId: string,
  theatreId: string,
): Promise<InvestigationProjectionResponse> {
  const { data } = await apiClient.post<InvestigationProjectionResponse>(
    `/api/v1/investigations/${investigationId}/remove-from-environment`,
    { theatre_id: theatreId },
  );
  return data;
}

// ── Admission (Workspace Phase 8 — FORGE admission bridge) ──────────────

export async function runAdmission(
  investigationId: string,
): Promise<AdmissionResult> {
  const { data } = await apiClient.post<AdmissionResult>(
    `/api/v1/investigations/${investigationId}/admission`,
  );
  return data;
}

// ── Shadow Bootstrap (Workspace Phase 8b) ────────────────────────────────

export async function runShadowBootstrap(
  investigationId: string,
): Promise<ShadowBootstrapResult> {
  const { data } = await apiClient.post<ShadowBootstrapResult>(
    `/api/v1/investigations/${investigationId}/shadow-bootstrap`,
  );
  return data;
}

// ── Shadow Execution (Workspace Phase 8b-next) ───────────────────────────

export async function runShadowExecution(
  investigationId: string,
): Promise<ShadowExecutionResult> {
  const { data } = await apiClient.post<ShadowExecutionResult>(
    `/api/v1/investigations/${investigationId}/shadow-execute`,
  );
  return data;
}

// ── Live Promotion (Workspace Phase 9) ────────────────────────────────────

export async function promoteLive(
  investigationId: string,
): Promise<PromotionResult> {
  const { data } = await apiClient.post<PromotionResult>(
    `/api/v1/investigations/${investigationId}/promote-live`,
  );
  return data;
}

// ── Readiness & Certificate Lifecycle ───────────────────────────────────

export async function fetchReadiness(
  investigationId: string,
): Promise<ReadinessResponse> {
  const { data } = await apiClient.get<ReadinessResponse>(
    `/api/v1/investigations/${investigationId}/readiness`,
  );
  return data;
}

export async function fetchCertificateRecord(
  investigationId: string,
): Promise<CertificateRecordResponse> {
  const { data } = await apiClient.get<CertificateRecordResponse>(
    `/api/v1/investigations/${investigationId}/certificate`,
  );
  return data;
}

export async function buildCertificate(
  investigationId: string,
): Promise<CertificateBuildResponse> {
  const { data } = await apiClient.post<CertificateBuildResponse>(
    `/api/v1/investigations/${investigationId}/certificate/build`,
  );
  return data;
}

export async function materializeTheatre(
  investigationId: string,
): Promise<Record<string, unknown>> {
  const { data } = await apiClient.post<Record<string, unknown>>(
    `/api/v1/investigations/${investigationId}/materialize-theatre`,
  );
  return data;
}

export async function anchorBatch(): Promise<AnchorBatchResponse> {
  const { data } = await apiClient.post<AnchorBatchResponse>(
    '/api/v1/investigations/certificates/anchor-batch',
  );
  return data;
}
